import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Avatar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ManagerDto from '../../interfaces/ManagerDto';
import './ManagersView.scss';

interface SearchFilters {
  Manager_FirstName: string;
  Manager_LastName: string;
  Manager_Email: string;
}

const initialFilters: SearchFilters = {
  Manager_FirstName: '',
  Manager_LastName: '',
  Manager_Email: ''
};

const ManagersView: React.FC = () => {
  const navigate = useNavigate();
  
  const [managers, setManagers] = useState<ManagerDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchManagers = useCallback(async (searchFilters = filters) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager/list`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch managers: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      let managersData = data.managers || [];

      // Apply client-side filtering since the API doesn't support filtering
      if (searchFilters.Manager_FirstName.trim()) {
        managersData = managersData.filter((manager: ManagerDto) =>
          manager.Manager_FirstName.toLowerCase().includes(searchFilters.Manager_FirstName.toLowerCase())
        );
      }
      if (searchFilters.Manager_LastName.trim()) {
        managersData = managersData.filter((manager: ManagerDto) =>
          manager.Manager_LastName.toLowerCase().includes(searchFilters.Manager_LastName.toLowerCase())
        );
      }
      if (searchFilters.Manager_Email.trim()) {
        managersData = managersData.filter((manager: ManagerDto) =>
          manager.Manager_Email.toLowerCase().includes(searchFilters.Manager_Email.toLowerCase())
        );
      }

      setManagers(managersData);
      setHasSearched(true);
      
    } catch (err) {
      console.error('Error fetching managers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load managers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchManagers(initialFilters);
  }, [fetchManagers]);

  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleSearch = () => {
    fetchManagers(filters);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setError(null);
    fetchManagers(initialFilters);
  };

  const handleManagerClick = (managerGuid: string) => {
    navigate(`/manager?Manager_guid=${managerGuid}`);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Active managers first, inactive (greyed) sorted to the bottom of the list (Lloyd, Jul 8).
  const sortedManagers = [...managers].sort(
    (a, b) => Number(b.Manager_Active) - Number(a.Manager_Active),
  );

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Manage Managers
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/create-manager')}
            color="primary"
          >
            Create Manager
          </Button>
        </Box>

        {/* Search Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Managers
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField
              label="First Name"
              value={filters.Manager_FirstName}
              onChange={(e) => handleFilterChange('Manager_FirstName', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
            />
            
            <TextField
              label="Last Name"
              value={filters.Manager_LastName}
              onChange={(e) => handleFilterChange('Manager_LastName', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
            />
            
            <TextField
              label="Email"
              value={filters.Manager_Email}
              onChange={(e) => handleFilterChange('Manager_Email', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 250, flex: 1 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              size="small"
              disabled={loading}
            >
              Search Managers
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Results */}
        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {hasSearched && (
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" color="textSecondary">
                    {managers.length === 0 ? 'No managers found' : `${managers.length} manager${managers.length === 1 ? '' : 's'} found`}
                  </Typography>
                </Box>
              )}
              
              {managers.length === 0 && hasSearched ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    No managers found matching your search criteria.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {sortedManagers.map((manager, index) => (
                    <React.Fragment key={manager.Manager_guid}>
                      <ListItem
                        onClick={() => handleManagerClick(manager.Manager_guid)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          cursor: 'pointer',
                          // Inactive staff stay visible but greyed (Lloyd, Jul 8) — click in to reactivate.
                          opacity: manager.Manager_Active ? 1 : 0.55,
                        }}
                      >
                        <Avatar sx={{ mr: 2, bgcolor: manager.Manager_Active ? 'primary.main' : 'grey.400' }}>
                          <PersonIcon />
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'medium' }}>
                                {manager.Manager_FirstName} {manager.Manager_LastName}
                              </Typography>
                              {!manager.Manager_Active && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    backgroundColor: 'error.main',
                                    color: 'white',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    fontSize: '0.7rem'
                                  }}
                                >
                                  INACTIVE
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="textSecondary">
                                {manager.Manager_Email}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < sortedManagers.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default ManagersView;
